import { Button } from 'react-bootstrap';

const csvValue = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

/**
 * Ações comuns de relatório. O exportador Excel usa CSV com BOM porque o Excel abre
 * esse formato nativamente e preserva acentos sem adicionar uma dependência de XLSX.
 */
const ReportExportButtons = ({
    contentRef,
    rows = [],
    headers = [],
    preambleRows = [],
    fileName = 'relatorio',
    documentTitle = fileName,
    // CSS adicional da janela de impressão. A janela é um documento novo, sem as folhas
    // de estilo da aplicação, então o relatório que tem visual próprio (logo, assinatura)
    // precisa mandar junto as regras que quer ver no papel.
    extraStyles = '',
    disabled = false,
    className = '',
    hideExcel = false
}) => {
    const exportToExcel = () => {
        const csv = '\ufeff' + [...preambleRows, headers, ...rows]
            .map(row => row.map(csvValue).join(';'))
            .join('\r\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fileName}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const exportToPdf = () => {
        const printWindow = window.open('', '_blank', 'width=1200,height=800');
        if (!printWindow || !contentRef?.current) return;

        // Imprime uma cópia independente do DOM da aplicação. Isso evita que estilos de
        // overflow, elementos ocultos ou o iframe do react-to-print produzam PDF vazio.
        printWindow.document.write(`<!doctype html>
            <html><head><meta charset="utf-8"><title>${documentTitle}</title>
            <style>
                @page { size: A4 landscape; margin: 12mm; }
                * { box-sizing: border-box; }
                body { margin: 0; padding: 0; color: #111; background: #fff; font-family: Arial, sans-serif; }
                h1 { margin: 0 0 8px; font-size: 22px; }
                p { margin: 0 0 4px; }
                .employee-statement__print-report, .employee-statement__detail { padding: 8px; color: #1f2937; background: #fff; }
                .employee-statement__detail-export-title { margin-bottom: 20px; padding: 0 0 12px; border-bottom: 2px solid #d32f2f; }
                .employee-statement__detail-export-title h1 { margin-bottom: 10px; color: #263238; font-size: 22px; }
                .employee-statement__detail-export-title p { color: #5f6368; font-size: 12px; }
                .employee-statement__section-title { margin: 20px 0 8px; padding: 7px 10px; color: #263238; background: #f3f4f6; border-left: 3px solid #d32f2f; font-size: 14px; }
                .employee-statement__section-title:first-of-type { margin-top: 0; }
                table { width: 100%; border-collapse: collapse; margin-top: 18px; font-size: 11px; }
                th, td { padding: 6px 8px; border: 1px solid #cfcfcf; text-align: left; }
                th { color: #263238; background: #eef1f4; font-weight: 700; }
                tbody tr:nth-child(even) td { background: #fafafa; }
                .employee-statement__print-branch { page-break-inside: avoid; margin-top: 20px; }
                .employee-statement__print-branch h2, .employee-statement__print-total-title { padding: 8px 10px; color: #263238; background: #f3f4f6; border-left: 3px solid #d32f2f; font-size: 15px; }
                .text-end { text-align: right; }
                /* Cabeçalho de dois blocos do extrato do colaborador. As regras carregam
                   o nome da tag porque a listra do zebrado (tbody tr:nth-child(even) td)
                   tem especificidade maior que uma classe sozinha. */
                thead th.print-group { text-align: center; font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; }
                thead th.print-group--revenue { background: #eef1f4; }
                thead th.print-group--earnings { background: #fdeadb; color: #9a4c07; }
                tbody tr td.print-emphasis, tbody tr th.print-emphasis, thead th.print-emphasis { background: #e6eaef !important; font-weight: 700; }
                thead th.print-split, tbody tr td.print-split, tbody tr th.print-split { border-left: 2px solid #e08a3c; }
                .d-block { display: block !important; }
                small { display: block; margin-top: 3px; color: #6b7280; font-size: 10px; }
                .employee-statement__print-source { width: 100%; height: auto; overflow: visible; visibility: visible; }
                .d-print-none { display: none !important; }
                .employee-statement__detail-export-title { display: block !important; }
                ${extraStyles}
            </style></head><body>${contentRef.current.innerHTML}</body></html>`);
        printWindow.document.close();
        printWindow.onafterprint = () => printWindow.close();
        let printStarted = false;

        /** Espera as imagens do documento (a logo) para elas não saírem em branco no PDF. */
        const waitForImages = () => {
            const images = Array.from(printWindow.document.images || []);
            const pending = images.filter(image => !image.complete);
            if (pending.length === 0) return Promise.resolve();
            return Promise.all(pending.map(image => new Promise(resolve => {
                image.addEventListener('load', resolve, { once: true });
                image.addEventListener('error', resolve, { once: true });
            })));
        };

        const print = () => {
            if (printStarted || printWindow.closed) return;
            printStarted = true;
            waitForImages().then(() => {
                if (printWindow.closed) return;
                printWindow.focus();
                printWindow.print();
            });
        };
        // Alguns navegadores disparam a impressão antes de terminar de montar o DOM da
        // nova janela, resultando em uma prévia vazia. O load e o fallback cobrem ambos.
        printWindow.onload = () => setTimeout(print, 100);
        setTimeout(() => {
            if (!printWindow.closed) print();
        }, 700);
    };

    return (
        <div className={`report-export-buttons ${className}`}>
            <Button
                variant="danger"
                className="report-export-button report-export-button--pdf"
                onClick={exportToPdf}
                disabled={disabled}
                title="Exportar PDF"
            >
                <span className="report-export-button__icon"><i className="fas fa-file-pdf"></i></span>
                <span>PDF</span>
            </Button>
            {!hideExcel && (
                <Button
                    variant="success"
                    className="report-export-button report-export-button--excel"
                    onClick={exportToExcel}
                    disabled={disabled}
                    title="Exportar Excel"
                >
                    <span className="report-export-button__icon"><i className="fas fa-file-excel"></i></span>
                    <span>Excel</span>
                </Button>
            )}
        </div>
    );
};

export default ReportExportButtons;
